/**
 * useGalgameController - Galgame 播放控制器
 *
 * 职责：
 * 1. 管理 Galgame 模式下的段落解析
 * 2. 控制步进导航（下一步、上一步）
 * 3. 控制消息间跳转
 *
 * 注意：所有状态存储在 useEngineStore.galgameState 中，
 * 本 Hook 只负责控制逻辑，不持有状态
 */
import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { logger } from '@/lib/logger'
import { useEngineStore } from '../stores/use-engine-store'
import type { GalgameSegments } from '../types/chat-model'

/**
 * 解析消息内容为段落
 */
function parseMessageToSegments(content: string): GalgameSegments {
  return content
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
}

export const useGalgameController = () => {
  // 从 Store 获取状态
  const messages = useEngineStore((s) => s.messages.messages)
  const { currentMessageIndex, galgameIndex, galgameSegments } = useEngineStore(
    useShallow((s) => ({
      currentMessageIndex: s.galgameState.currentMessageIndex,
      galgameIndex: s.galgameState.galgameIndex,
      galgameSegments: s.galgameState.galgameSegments,
    }))
  )
  const { updateGalgameState, resetGalgameState } = useEngineStore(
    useShallow((s) => ({
      updateGalgameState: s.galgameState.updateGalgameState,
      resetGalgameState: s.galgameState.resetGalgameState,
    }))
  )

  /**
   * 初始化段落
   * 将指定消息的内容解析为段落数组
   *
   * @param msgIndex - 消息索引，默认为最后一条消息
   * @param segmentIndex - 段落索引，默认为 0
   */
  const initSegments = useCallback(
    (msgIndex?: number, segmentIndex?: number) => {
      // 直接从 Store 读取最新消息，避免闭包延迟导致的数据不一致
      const currentMessages = useEngineStore.getState().messages.messages
      if (!currentMessages || currentMessages.length === 0) return

      const targetIndex = msgIndex ?? currentMessages.length - 1
      const targetMsg = currentMessages[targetIndex]

      if (!targetMsg) return

      const segments = parseMessageToSegments(targetMsg.content)
      logger.debug('msgIndex:', targetIndex)
      updateGalgameState({
        currentMessageIndex: targetIndex,
        galgameSegments: segments,
        galgameIndex: segmentIndex ?? 0,
      })
    },
    [updateGalgameState]
  )

  /**
   * 下一个段落
   */
  const nextSegmentStep = useCallback(() => {
    if (galgameSegments && galgameIndex < galgameSegments.length - 1) {
      updateGalgameState({ galgameIndex: galgameIndex + 1 })
    }
  }, [galgameIndex, galgameSegments, updateGalgameState])

  /**
   * 上一个段落
   */
  const lastSegmentStep = useCallback(() => {
    if (galgameIndex > 0) {
      updateGalgameState({ galgameIndex: galgameIndex - 1 })
    }
  }, [galgameIndex, updateGalgameState])

  /**
   * 跳转到下一条消息
   */
  const jumpToNextMessage = useCallback(() => {
    if (messages && currentMessageIndex < messages.length - 1) {
      const nextIndex = currentMessageIndex + 1
      const nextMsg = messages[nextIndex]
      if (nextMsg) {
        const segments = parseMessageToSegments(nextMsg.content)
        updateGalgameState({
          currentMessageIndex: nextIndex,
          galgameSegments: segments,
          galgameIndex: 0,
        })
      }
    }
  }, [messages, currentMessageIndex, updateGalgameState])

  /**
   * 跳转到上一条消息
   */
  const jumpToLastMessage = useCallback(() => {
    if (currentMessageIndex > 0 && messages) {
      const prevIndex = currentMessageIndex - 1
      const prevMsg = messages[prevIndex]
      if (prevMsg) {
        const segments = parseMessageToSegments(prevMsg.content)
        updateGalgameState({
          currentMessageIndex: prevIndex,
          galgameSegments: segments,
          galgameIndex: 0,
        })
      }
    }
  }, [currentMessageIndex, messages, updateGalgameState])

  /**
   * 下一步（智能判断：段落内步进 or 跳转下一消息）
   */
  const nextStep = useCallback(() => {
    if (galgameSegments && galgameIndex < galgameSegments.length - 1) {
      // 当前消息还有未显示的段落，显示下一段
      nextSegmentStep()
    } else {
      // 当前消息段落已全部显示，跳转到下一条消息
      jumpToNextMessage()
    }
  }, [galgameIndex, galgameSegments, nextSegmentStep, jumpToNextMessage])

  /**
   * 上一步（智能判断：段落内回退 or 跳转上一消息末尾）
   */
  const lastStep = useCallback(() => {
    if (galgameIndex > 0) {
      // 当前消息还可以回退段落
      lastSegmentStep()
    } else if (messages && currentMessageIndex > 0) {
      // 当前消息已在第一段，跳转到上一条消息的最后一段
      const prevMsgIndex = currentMessageIndex - 1
      const prevMsg = messages[prevMsgIndex]
      if (prevMsg) {
        const segments = parseMessageToSegments(prevMsg.content)
        updateGalgameState({
          currentMessageIndex: prevMsgIndex,
          galgameSegments: segments,
          galgameIndex: segments.length - 1,
        })
      }
    }
  }, [
    galgameIndex,
    currentMessageIndex,
    messages,
    lastSegmentStep,
    updateGalgameState,
  ])

  /**
   * 获取当前显示的段落内容
   */
  const getCurrentSegment = useCallback(() => {
    if (!galgameSegments || galgameSegments.length === 0) return ''
    return galgameSegments[galgameIndex] || ''
  }, [galgameSegments, galgameIndex])

  /**
   * 获取已显示的所有段落（用于渲染已播放的文本）
   */
  const getRevealedSegments = useCallback(() => {
    if (!galgameSegments) return []
    return galgameSegments.slice(0, galgameIndex + 1)
  }, [galgameSegments, galgameIndex])

  /**
   * 检查是否可以前进
   */
  const canGoNext = useCallback(() => {
    if (!messages || messages.length === 0) return false
    // 如果当前消息还有段落没显示完，可以前进
    if (galgameSegments && galgameIndex < galgameSegments.length - 1)
      return true
    // 如果还有下一条消息，可以前进
    return currentMessageIndex < messages.length - 1
  }, [messages, galgameSegments, galgameIndex, currentMessageIndex])

  /**
   * 检查是否可以后退
   */
  const canGoBack = useCallback(() => {
    // 如果当前段落不是第一段，可以后退
    if (galgameIndex > 0) return true
    // 如果当前消息不是第一条，可以后退
    return currentMessageIndex > 0
  }, [galgameIndex, currentMessageIndex])

  return useMemo(
    () => ({
      // 状态（直接从 store 读取）
      currentMessageIndex,
      galgameIndex,
      galgameSegments,

      // 控制函数
      initSegments,
      nextStep,
      lastStep,
      nextSegmentStep,
      lastSegmentStep,
      jumpToNextMessage,
      jumpToLastMessage,
      resetGalgameState,

      // 辅助函数
      getCurrentSegment,
      getRevealedSegments,
      canGoNext,
      canGoBack,
    }),
    [
      currentMessageIndex,
      galgameIndex,
      galgameSegments,
      initSegments,
      nextStep,
      lastStep,
      nextSegmentStep,
      lastSegmentStep,
      jumpToNextMessage,
      jumpToLastMessage,
      resetGalgameState,
      getCurrentSegment,
      getRevealedSegments,
      canGoNext,
      canGoBack,
    ]
  )
}
